import React, { useState, useRef, useEffect } from 'react';
import { BottomSheet } from '@/components/BottomSheet';

export interface FilterOption {
  key: string;
  label: string;
  value: string | boolean;
}

export interface FilterGroup {
  title: string;
  key: string;
  options: FilterOption[];
  multiple?: boolean;
  icon?: string; // Icon class name (e.g., 'ri-building-2-line')
}

interface FilterPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: Record<string, any>) => void;
  onClear: () => void;
  filterGroups: FilterGroup[];
  currentFilters?: Record<string, any>;
}

const FilterPopup: React.FC<FilterPopupProps> = ({
  isOpen,
  onClose,
  onApply,
  onClear,
  filterGroups,
  currentFilters = {},
}) => {
  const [localFilters, setLocalFilters] = useState<Record<string, any>>({});
  const [selectedCategory, setSelectedCategory] = useState<string>(
    filterGroups[0]?.key || ''
  );
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isMobile, setIsMobile] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setLocalFilters(currentFilters);
    }
  }, [isOpen, currentFilters]);

  useEffect(() => {
    if (
      filterGroups.length > 0 &&
      (!selectedCategory ||
        !filterGroups.find((g) => g.key === selectedCategory))
    ) {
      setSelectedCategory(filterGroups[0].key);
    }
  }, [filterGroups, selectedCategory]);

  // Reset search term when category changes
  useEffect(() => {
    setSearchTerm('');
  }, [selectedCategory]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleFilterChange = (
    groupKey: string,
    optionValue: string | boolean,
    isMultiple: boolean
  ) => {
    setLocalFilters((prev) => {
      const newFilters = { ...prev };

      if (isMultiple) {
        const currentValues = Array.isArray(newFilters[groupKey])
          ? newFilters[groupKey]
          : [];
        const stringValue = String(optionValue);

        if (currentValues.includes(stringValue)) {
          const updatedValues = currentValues.filter(
            (v: any) => String(v) !== stringValue
          );
          if (updatedValues.length === 0) {
            delete newFilters[groupKey];
          } else {
            newFilters[groupKey] = updatedValues;
          }
        } else {
          newFilters[groupKey] = [...currentValues, stringValue];
        }
      } else {
        const stringValue = String(optionValue);
        if (String(newFilters[groupKey]) === stringValue) {
          delete newFilters[groupKey];
        } else {
          newFilters[groupKey] = stringValue;
        }
      }

      return newFilters;
    });
  };

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleClear = () => {
    setLocalFilters({});
    onClear();
    onClose();
  };

  const isOptionSelected = (
    groupKey: string,
    optionValue: string | boolean,
    isMultiple: boolean
  ): boolean => {
    const stringValue = String(optionValue);

    if (isMultiple) {
      const values = Array.isArray(localFilters[groupKey])
        ? localFilters[groupKey]
        : [];
      return values.some((v: any) => String(v) === stringValue);
    }

    return String(localFilters[groupKey]) === stringValue;
  };

  if (!isOpen && !isMobile) return null;

  const selectedGroup = filterGroups.find(
    (group) => group.key === selectedCategory
  );

  // Filter options based on search term
  const filteredOptions =
    selectedGroup?.options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setSearchTerm('');
    }
  };

  // Highlight search term in option labels
  const highlightText = (text: string, highlight: string) => {
    if (!highlight.trim()) {
      return <span>{text}</span>;
    }

    const regex = new RegExp(`(${highlight})`, 'gi');
    const parts = text.split(regex);

    return (
      <span>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <mark
              key={index}
              className="bg-yellow-200 text-gray-900 px-0.5 rounded"
            >
              {part}
            </mark>
          ) : (
            <span key={index}>{part}</span>
          )
        )}
      </span>
    );
  };

  // Shared: category tabs + options content
  const categoryTabs = (
    <div className="flex gap-2 overflow-x-auto px-4 py-3 border-b border-gray-200 shrink-0">
      {filterGroups.map((group) => {
        const isActive = selectedCategory === group.key;
        const hasSelectedFilters =
          Array.isArray(localFilters[group.key]) &&
          localFilters[group.key].length > 0;
        return (
          <button
            key={group.key}
            onClick={() => setSelectedCategory(group.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-150 border ${
              isActive
                ? 'bg-primary text-white border-primary'
                : 'bg-white text-gray-600 border-gray-300 hover:border-primary hover:text-primary'
            }`}
          >
            {group.icon && <i className={`${group.icon} text-sm`}></i>}
            {group.title}
            {hasSelectedFilters && (
              <i className="ri-checkbox-circle-fill text-xs ml-0.5"></i>
            )}
          </button>
        );
      })}
    </div>
  );

  const optionsContent = (
    <>
      {/* Options header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200">
        {selectedGroup && (
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {selectedGroup.icon && (
                <i
                  className={`${selectedGroup.icon} text-primary mr-2 text-base`}
                ></i>
              )}
              <span className="text-sm font-semibold text-gray-900">
                {selectedGroup.title}
              </span>
            </div>
            {selectedGroup.multiple && filteredOptions.length > 0 && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const allFilteredSelected = filteredOptions.every((opt) =>
                    isOptionSelected(selectedGroup.key, opt.value, true)
                  );
                  if (allFilteredSelected) {
                    setLocalFilters((prev) => {
                      const newFilters = { ...prev };
                      const currentValues = Array.isArray(
                        newFilters[selectedGroup.key]
                      )
                        ? newFilters[selectedGroup.key]
                        : [];
                      const filteredValues = filteredOptions.map((opt) =>
                        String(opt.value)
                      );
                      const remainingValues = currentValues.filter(
                        (v: any) => !filteredValues.includes(String(v))
                      );
                      if (remainingValues.length === 0) {
                        delete newFilters[selectedGroup.key];
                      } else {
                        newFilters[selectedGroup.key] = remainingValues;
                      }
                      return newFilters;
                    });
                  } else {
                    const filteredValues = filteredOptions.map((opt) =>
                      String(opt.value)
                    );
                    setLocalFilters((prev) => {
                      const currentValues = Array.isArray(
                        prev[selectedGroup.key]
                      )
                        ? prev[selectedGroup.key]
                        : [];
                      const mergedValues = Array.from(
                        new Set([...currentValues, ...filteredValues])
                      );
                      return { ...prev, [selectedGroup.key]: mergedValues };
                    });
                  }
                }}
                className="text-xs text-gray-500 hover:text-gray-700 underline font-medium"
              >
                {filteredOptions.every((opt) =>
                  isOptionSelected(selectedGroup.key, opt.value, true)
                )
                  ? `Deselect All${searchTerm ? ' Visible' : ''}`
                  : `Select All${searchTerm ? ' Visible' : ''}`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Search */}
      {selectedGroup && (
        <div className="flex-shrink-0 border-b border-gray-100">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              placeholder={`Search ${selectedGroup.title.toLowerCase()}...`}
              className="w-full pl-8 pr-8 py-2 text-sm border-0 focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                type="button"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Scrollable options */}
      <div className="flex-1 overflow-y-auto p-4">
        {selectedGroup && (
          <div
            key={`filter-content-${selectedGroup.key}`}
            className="space-y-1"
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = isOptionSelected(
                  selectedGroup.key,
                  option.value,
                  selectedGroup.multiple || false
                );
                return (
                  <label
                    key={`filter-${selectedGroup.key}-option-${option.value}-${option.key}`}
                    className="flex items-center cursor-pointer group py-2"
                  >
                    <div className="relative mr-3 flex-shrink-0">
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={isSelected}
                        value={String(option.value)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleFilterChange(
                            selectedGroup.key,
                            option.value,
                            selectedGroup.multiple || false
                          );
                        }}
                      />
                      <div
                        className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-all duration-200 ${
                          isSelected
                            ? 'bg-primary border-primary shadow-sm'
                            : 'bg-white border-gray-300 group-hover:border-primary'
                        }`}
                      >
                        {isSelected && (
                          <i className="ri-check-line text-white text-sm font-bold"></i>
                        )}
                      </div>
                    </div>
                    <span
                      className={`text-sm font-medium transition-colors duration-150 ${
                        isSelected ? 'text-gray-900' : 'text-gray-700'
                      }`}
                    >
                      {highlightText(option.label, searchTerm)}
                    </span>
                  </label>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <i className="ri-search-line text-2xl mb-2 text-gray-400"></i>
                <p className="text-sm">No options found</p>
                <p className="text-xs text-gray-400 mt-1">
                  Try adjusting your search term
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 flex items-center gap-2 border-t border-gray-200 shrink-0">
        <button
          onClick={handleClear}
          className="flex-1 text-sm text-gray-600 hover:text-gray-800 rounded-lg border border-gray-300 font-medium transition-colors duration-150 py-2"
        >
          Clear
        </button>
        <button
          onClick={handleApply}
          className="flex-1 h-9 text-sm bg-primary text-white rounded-lg bg-primary-hover transition-all duration-200 font-medium"
        >
          Apply
        </button>
      </div>
    </>
  );

  // ── Mobile: BottomSheet ──────────────────────────────────────────────────
  if (isMobile) {
    return (
      <BottomSheet isOpen={isOpen} onClose={onClose} title="Filter">
        <div className="flex flex-col">
          {categoryTabs}
          {optionsContent}
        </div>
      </BottomSheet>
    );
  }

  // ── Desktop: positioned dropdown ────────────────────────────────────────
  if (!isOpen) return null;

  return (
    <div
      ref={popupRef}
      className="absolute top-full left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-0 mt-2 bg-white rounded-xl shadow-2xl w-[95vw] sm:w-[696px] max-w-[95vw] flex overflow-hidden z-50 border border-gray-200"
    >
      {/* Left Sidebar - Categories */}
      <div className="w-40 sm:w-56 bg-white flex flex-col border-r border-gray-200">
        <div className="p-3 flex-1">
          {filterGroups.map((group) => {
            const isActive = selectedCategory === group.key;
            const hasSelectedFilters =
              Array.isArray(localFilters[group.key]) &&
              localFilters[group.key].length > 0;

            return (
              <button
                key={group.key}
                onClick={() => setSelectedCategory(group.key)}
                className={`w-full text-left p-3 rounded-lg mb-1 transition-all duration-150 min-h-[48px] flex items-center ${
                  isActive
                    ? 'bg-gray-100 border border-gray-200 text-gray-800'
                    : 'hover:bg-white text-gray-600'
                }`}
              >
                <div className="flex items-center text-sm w-full">
                  {group.icon && (
                    <i
                      className={`${
                        group.icon
                      } text-base mr-2 mt-0.5 flex-shrink-0 transition-transform duration-150 ${
                        isActive ? 'text-primary' : 'text-gray-400'
                      }`}
                    ></i>
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="font-medium leading-tight text-xs sm:text-sm break-words max-w-[120px] sm:max-w-[160px] line-clamp-2">
                      {group.title}
                    </span>
                  </div>
                  {hasSelectedFilters && (
                    <i className="ri-checkbox-circle-fill text-primary ml-2 flex-shrink-0 mt-0.5"></i>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Right Content - Filter Options */}
      <div className="flex flex-col bg-white" style={{ height: '396px' }}>
        {/* Header */}
        <div className="flex-shrink-0 p-5 border-b border-gray-200">
          {selectedGroup && (
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {selectedGroup.icon && (
                  <i
                    className={`${selectedGroup.icon} text-primary mr-2 text-base`}
                  ></i>
                )}
                <span className="text-base font-medium text-gray-900 break-words max-w-[200px] line-clamp-2 leading-tight">
                  {selectedGroup.title}
                </span>
              </div>
              {selectedGroup.multiple && filteredOptions.length > 0 && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    const allFilteredSelected = filteredOptions.every((opt) =>
                      isOptionSelected(selectedGroup.key, opt.value, true)
                    );

                    if (allFilteredSelected) {
                      // Deselect only the filtered options
                      setLocalFilters((prev) => {
                        const newFilters = { ...prev };
                        const currentValues = Array.isArray(
                          newFilters[selectedGroup.key]
                        )
                          ? newFilters[selectedGroup.key]
                          : [];
                        const filteredValues = filteredOptions.map((opt) =>
                          String(opt.value)
                        );
                        const remainingValues = currentValues.filter(
                          (v: any) => !filteredValues.includes(String(v))
                        );

                        if (remainingValues.length === 0) {
                          delete newFilters[selectedGroup.key];
                        } else {
                          newFilters[selectedGroup.key] = remainingValues;
                        }
                        return newFilters;
                      });
                    } else {
                      // Select all filtered options (merge with existing)
                      const filteredValues = filteredOptions.map((opt) =>
                        String(opt.value)
                      );
                      setLocalFilters((prev) => {
                        const currentValues = Array.isArray(
                          prev[selectedGroup.key]
                        )
                          ? prev[selectedGroup.key]
                          : [];
                        const mergedValues = Array.from(
                          new Set([...currentValues, ...filteredValues])
                        );
                        return {
                          ...prev,
                          [selectedGroup.key]: mergedValues,
                        };
                      });
                    }
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700 underline font-medium"
                >
                  {filteredOptions.every((opt) =>
                    isOptionSelected(selectedGroup.key, opt.value, true)
                  )
                    ? `Deselect All${searchTerm ? ' Visible' : ''}`
                    : `Select All${searchTerm ? ' Visible' : ''}`}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Search Field */}
        {selectedGroup && (
          <div className="flex-shrink-0 border-b border-gray-100">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                placeholder={`Search ${selectedGroup.title.toLowerCase()}...`}
                className="w-full pl-8 pr-8 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  type="button"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Scrollable Options */}
        <div className="flex-1 overflow-y-auto p-5">
          {selectedGroup && (
            <div
              key={`filter-content-${selectedGroup.key}`}
              className="space-y-2"
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const isSelected = isOptionSelected(
                    selectedGroup.key,
                    option.value,
                    selectedGroup.multiple || false
                  );

                  return (
                    <label
                      key={`filter-${selectedGroup.key}-option-${option.value}-${option.key}`}
                      className="flex items-start cursor-pointer group py-2"
                    >
                      <div className="relative mr-4 mt-0.5 flex-shrink-0">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={isSelected}
                          value={String(option.value)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleFilterChange(
                              selectedGroup.key,
                              option.value,
                              selectedGroup.multiple || false
                            );
                          }}
                        />
                        <div
                          className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-all duration-200 ${
                            isSelected
                              ? 'bg-primary border-primary shadow-sm'
                              : 'bg-white border-gray-300 group-hover:border-primary'
                          }`}
                        >
                          {isSelected && (
                            <i className="ri-check-line text-white text-sm font-bold"></i>
                          )}
                        </div>
                      </div>
                      <span
                        className={`text-sm font-medium transition-colors duration-150 break-words max-w-[320px] line-clamp-3 leading-relaxed ${
                          isSelected ? 'text-gray-900' : 'text-gray-700'
                        }`}
                      >
                        {highlightText(option.label, searchTerm)}
                      </span>
                    </label>
                  );
                })
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                  <i className="ri-search-line text-2xl mb-2 text-gray-400"></i>
                  <p className="text-sm">No options found</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Try adjusting your search term
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Buttons */}
        <div className="px-2 py-2 flex items-center gap-1">
          <button
            onClick={handleClear}
            className="flex-1 bg-white w-[208px] text-sm text-gray-600 hover:text-gray-800 rounded-lg border border-gray-300 font-medium transition-colors duration-150 py-2"
          >
            Clear
          </button>
          <button
            onClick={handleApply}
            className="flex-1 h-9 text-sm w-[208px] bg-primary text-white rounded-lg bg-primary-hover transition-all duration-200 font-medium"
            style={{ width: '208px' }}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterPopup;
